import { Box, Paper, Typography } from '@mui/material';
import { BASIC_BORDER_COLOR } from '../../../../themes/tokens';
import {
  ResponsiveContainer,
  BarChart,
  XAxis,
  YAxis,
  Bar,
  Cell,
  Tooltip,
  CartesianGrid,
} from 'recharts';
import { CustomTooltip } from '../CustomTooltip/CustomTooltip';
import { ChartData, MultipleKpisChartData } from './types';
import { Trans, useTranslation } from 'react-i18next';
import { useResponsive } from '../../../../hooks/useResponsive';
import { getBarColor } from './helpers';

type Props = {
  title: string;
  data: MultipleKpisChartData[];
};

export const MultipleKpisChart = ({ title, data }: Props) => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();

  const chartData: ChartData[] = data.map((item) => {
    return { ...item, name: t(`dashboard.kpi.${item.field}`) };
  });

  return (
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        minHeight: {
          xs: '550px',
          sm: '550px',
          md: '100%',
        },
        height: '100%',
        p: 2,
        borderRadius: '8px',
        bgcolor: '#f8f7f6',
        border: `1px solid ${BASIC_BORDER_COLOR}`,
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        padding: '16px 8px',
      }}
    >
      <Box
        display="flex"
        flexDirection={isMobile ? 'column' : 'row'}
        justifyContent="space-between"
      >
        <Typography sx={{ flex: 1 }} variant="h6">
          {title}
        </Typography>
        <Typography sx={{ fontSize: 10, flex: 1 }}>
          <Trans
            i18nKey="dashboard.normalizedValuesNote"
            components={{
              bold: <strong />,
            }}
          />
        </Typography>
      </Box>
      <ResponsiveContainer width="100%">
        <BarChart
          data={chartData}
          layout="vertical"
          margin={{ left: 5, right: 10 }}
          barCategoryGap={2}
          barGap={2}
        >
          <XAxis
            type="number"
            domain={[0, 100]}
            ticks={[0, 20, 40, 60, 80, 100]}
            hide={true}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            type="category"
            dataKey="name"
            width={isMobile ? 100 : 220}
            tick={{ fontSize: 11 }}
            tickFormatter={(value) => (isMobile ? value : value.replace(/ /g, '\u00A0'))}
            axisLine={false}
            tickLine={false}
          />
          <CartesianGrid horizontal={false} vertical strokeDasharray="1" />
          <Tooltip cursor={{ fill: 'rgba(0,0,0,0.04)' }} content={<CustomTooltip />} />

          <Bar dataKey="value" barSize={7}>
            {chartData.map((entry, index) => {
              return <Cell key={index} fill={getBarColor(entry)} />;
            })}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Paper>
  );
};
